import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { NoopComponent } from './components/noop.component';
import { AuthGuard } from './guards/auth.guard';
import { InitialNavGuard } from './guards/initial-nav.guard';
import { LoginGuard } from './guards/login.guard';
import { NonAuthGuard } from './guards/non-auth.guard';
import { PincodeGuard } from './security/pincode-guard.service';

export const routes: Routes = [
  { path: '', redirectTo: 'weguide-welcome', pathMatch: 'full' },
  {
    path: ':organization/token/:token/:participant_login',
    loadChildren: () => import('./pages/home/home.module').then(m => m.HomePageModule),
  },
  {
    path: 'questionnaire/:engagement',
    loadChildren: () => import('./pages/questionnaire/questionnaire.module').then(m => m.QuestionnairePageModule),
    canActivate: [AuthGuard, PincodeGuard],
  },
  {
    path: 'tabs',
    loadChildren: () => import('./pages/tabs/tabs.module').then(m => m.TabsPageModule),
    canActivate: [AuthGuard, PincodeGuard],
  },
  {
    path: 'welcome',
    loadChildren: () => import('./pages/welcome/welcome.module').then(m => m.WelcomePageModule),
    canActivate: [AuthGuard],
    data: { nopincode: true },
  },
  { path: 'error', loadChildren: () => import('./pages/error/error.module').then(m => m.ErrorPageModule) },
  { path: 'error/:code', loadChildren: () => import('./pages/error/error.module').then(m => m.ErrorPageModule) },
  {
    path: 'weguide-welcome',
    loadChildren: () => import('./pages/weguide-welcome/weguide-welcome.module').then(m => m.WeguideWelcomePageModule),
  },
  {
    path: 'program-code/:code',
    loadChildren: () => import('./pages/program-code/program-code.module').then(m => m.ProgramCodePageModule),
    canActivate: [AuthGuard, PincodeGuard],
  },
  {
    path: 'program-code',
    loadChildren: () => import('./pages/program-code/program-code.module').then(m => m.ProgramCodePageModule),
    canActivate: [AuthGuard, PincodeGuard],
  },
  {
    path: 'program-details/:code',
    loadChildren: () => import('./pages/program-details/program-details.module').then(m => m.ProgramDetailsPageModule),
    canActivate: [AuthGuard, PincodeGuard],
  },
  {
    path: 'terms-security',
    loadChildren: () => import('./pages/terms-security/terms-security.module').then(m => m.TermsSecurityPageModule),
    canActivate: [AuthGuard],
    data: { nopincode: true },
  },
  {
    path: 'disclaimer',
    loadChildren: () => import('./pages/disclaimer/disclaimer.module').then(m => m.DisclaimerPageModule),
    canActivate: [AuthGuard],
    data: { nopincode: true },
  },
  { path: 'signup/:code', loadChildren: () => import('./pages/signup/signup.module').then(m => m.SignupPageModule) },
  {
    path: 'landing',
    canActivate: [NonAuthGuard],
    loadChildren: () => import('./pages/landing/landing.module').then(m => m.LandingPageModule),
  },
  {
    path: 'register',
    canActivate: [NonAuthGuard],
    loadChildren: () => import('./pages/signup/signup.module').then(m => m.SignupPageModule),
  },
  {
    path: 'login',
    canActivate: [LoginGuard],
    component: NoopComponent,
  },
  {
    path: 'signin',
    canActivate: [NonAuthGuard],
    loadChildren: () => import('./pages/login/login.module').then(m => m.LoginPageModule),
  },
  {
    path: 'verification',
    loadChildren: () => import('./pages/verification/verification.module').then(m => m.VerificationPageModule),
  },
  {
    path: 'tenant-information',
    loadChildren: () => import('./pages/tenant-information/tenant-information.module').then(m => m.TenantInformationPageModule),
    canActivate: [AuthGuard, PincodeGuard],
  },
  {
    path: 'about',
    loadChildren: () => import('./pages/about/about.module').then(m => m.AboutPageModule),
    canActivate: [AuthGuard, PincodeGuard],
  },
  {
    path: 'birth-date-verification',
    loadChildren: () =>
      import('./pages/birth-date-verification/birth-date-verification.module').then(m => m.BirthDateVerificationPageModule),
    canActivate: [AuthGuard],
    data: { nopincode: true },
  },
  {
    path: ':organization/invalid_token',
    loadChildren: () => import('./pages/invalid-token/invalid-token.module').then(m => m.InvalidTokenPageModule),
  },
  {
    path: 'information-overview/:section',
    loadChildren: () => import('./pages/information-overview/information-overview.module').then(m => m.InformationOverviewPageModule),
    canActivate: [AuthGuard, PincodeGuard],
  },
  {
    path: 'pick-program',
    loadChildren: () => import('./pages/pick-program/pick-program.module').then(m => m.PickProgramPageModule),
    canActivate: [AuthGuard, PincodeGuard],
  },
  {
    path: 'pick-program-select',
    loadChildren: () => import('./pages/pick-program-select/pick-program-select.module').then(m => m.PickProgramSelectPageModule),
    canActivate: [AuthGuard, PincodeGuard],
  },
  {
    path: 'program-overview',
    loadChildren: () => import('./pages/program-overview/program-overview.module').then(m => m.ProgramOverviewPageModule),
    canActivate: [AuthGuard, PincodeGuard],
  },
  {
    path: 'styleguide',
    loadChildren: () => import('./pages/styleguide/styleguide.module').then(m => m.StyleguidePageModule),
  },
  {
    path: 'styleguide-navs-tabs',
    loadChildren: () => import('./pages/styleguide-navs-tabs/styleguide-navs-tabs.module').then(m => m.StyleguideNavsTabsPageModule),
  },
  {
    path: 'styleguide-illustrations',
    loadChildren: () =>
      import('./pages/styleguide-illustrations/styleguide-illustrations.module').then(m => m.StyleguideIllustrationsPageModule),
  },
  {
    path: 'goals',
    loadChildren: () => import('./pages/tabs/goals/goals.module').then(m => m.GoalsPageModule),
    canActivate: [AuthGuard, PincodeGuard],
  },
  {
    // for backward compatibility
    path: 'pick-platform',
    redirectTo: 'initial',
  },
  {
    path: 'initial',
    component: NoopComponent,
    canActivate: [InitialNavGuard],
  },
  {
    path: 'program-overview',
    loadChildren: () => import('./pages/program-overview/program-overview.module').then(m => m.ProgramOverviewPageModule),
    canActivate: [AuthGuard, PincodeGuard],
  },
  {
    path: 'garmin-connect',
    loadChildren: () => import('./pages/garmin-connect/garmin-connect.module').then(m => m.GarminConnectPageModule),
    canActivate: [AuthGuard, PincodeGuard],
  },
  {
    path: 'garmin-successful',
    loadChildren: () => import('./pages/garmin-connect/garmin-successful/garmin-successful.module').then(m => m.GarminSuccessfulPageModule),
    canActivate: [AuthGuard, PincodeGuard],
  },
  {
    path: 'reset',
    loadChildren: () => import('./pages/reset-pin/reset-pin.module').then(m => m.ResetPinPageModule),
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
})
export class AppRoutingModule {}
